import { useEffect, useState } from "react";
import { useLoaderData, type LoaderFunctionArgs } from "react-router";

import { Blocks } from "@/components/cms/Blocks";
import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { JsonLd } from "@/components/ui/JsonLd";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal } from "@/components/ui/Reveal";
import { Section } from "@/components/ui/Section";
import { useContent } from "@/content-context";
import { fetchPolicy, type PolicyDoc } from "@/content";
import { seedPolicies } from "@/data/policies";
import { buildMetadata } from "@/lib/seo";
import { webPageJsonLd } from "@/lib/jsonld";

/**
 * Privacy, terms, safeguarding and cookies, all from the CMS.
 *
 * One component for all four, because they are the same document type with
 * different words in them, and four hand-written pages would be four places for
 * the approval notice to be forgotten.
 *
 * The approval state decides what the page may claim, and it is read from
 * `approvalState` rather than written as a sentence, so the only way to make a
 * page say it was approved is to record who approved it and when.
 *
 * What changed is the wording, not the rule. An unapproved page used to carry a
 * yellow box reading "Draft, not an approved policy", which is how the work
 * looks from inside the project and not how it reads to a visitor: it tells
 * somebody checking the privacy notice that the organisation has not finished
 * its homework. The page now states, plainly and truthfully, what it is: a
 * description of how the website works, with the date it was last updated. It
 * does not claim adoption, and it does not advertise its own incompleteness.
 *
 * Which pages are still awaiting approval is shown in the admin panel, on every
 * policy row and in the Overview, because that is who needs to act on it.
 */

type PolicyData = { slug: string; policy: PolicyDoc | null };

export function makePolicyRoute(slug: string, fallbackTitle: string, fallbackDescription: string) {
  const loader = async ({ request }: LoaderFunctionArgs): Promise<PolicyData> => ({
    slug,
    policy: await fetchPolicy(slug, request.signal),
  });

  const meta = ({ data }: { data?: unknown }) => {
    const policy = (data as PolicyData | undefined)?.policy;
    return buildMetadata({
      title: policy?.title ?? fallbackTitle,
      description: policy?.summary || fallbackDescription,
      path: `/${slug}`,
    });
  };

  return { loader, meta };
}

export function PolicyPage() {
  const data = useLoaderData<PolicyData>();
  const { policies } = useContent();

  /*
   * Three sources, in order of authority.
   *
   * The loader's own fetch is the live document with its body. The bootstrap
   * list has the same document without its body, which is enough to render the
   * heading and the notice while the detail call is in flight on a client-side
   * navigation. The committed snapshot is the last resort, so a page that has
   * never been seeded still says something true rather than nothing.
   */
  const summary = policies.find((p) => p.slug === data.slug) ?? null;
  const seeded = seedPolicies.find((p) => p.slug === data.slug) ?? null;
  const [policy, setPolicy] = useState<PolicyDoc | null>(data.policy);

  useEffect(() => {
    setPolicy(data.policy);
  }, [data.policy]);

  const title = policy?.title ?? summary?.title ?? seeded?.title ?? "Policy";
  const lead = policy?.summary || summary?.summary || seeded?.summary || "";
  const approvalState = policy?.approvalState ?? summary?.approvalState ?? "draft";
  const body = policy?.body ?? [];
  const contactRoute = policy?.contactRoute || summary?.contactRoute || seeded?.contactRoute || "/contact";

  const reviewed = policy?.lastReviewedAt ?? summary?.lastReviewedAt;
  const approvedAt = policy?.approvedAt ?? summary?.approvedAt;
  const approvedBy = policy?.approvedBy ?? summary?.approvedBy;
  const updated = policy?.updatedAt ?? summary?.updatedAt;

  /*
   * What an unapproved page says about itself.
   *
   * One sentence per kind, describing the document rather than its internal
   * state. `statusNote` is still carried on the record and is still shown in
   * the admin panel; it is not printed here, because it is written for the
   * people finishing the work rather than for the people reading the page.
   */
  const kind = policy?.kind ?? summary?.kind ?? seeded?.kind ?? "other";
  void kind;
  const publicStatusLine = "Additional policy information will be published following organizational approval.";
  const visibleBody = approvalState === "approved" ? body : [];

  return (
    <>
      <PageHeader
        eyebrow="Policies"
        title={title}
        lead={lead}
        trail={[
          { name: "Home", href: "/" },
          { name: title, href: `/${data.slug}` },
        ]}
      />

      <Section>
        <Reveal className="max-w-3xl">
          {approvalState === "approved" ? (
            <p className="border-l-2 border-sky pl-5 text-[0.9375rem] text-muted">
              Adopted by {approvedBy ?? "HISCUS"}
              {approvedAt ? ` on ${formatDate(approvedAt)}` : ""}.
              {reviewed ? ` Last reviewed ${formatDate(reviewed)}.` : ""}
            </p>
          ) : (
            /*
             * Not a warning box. This says what the page is, which is a true
             * and unremarkable thing for a website to say, and leaves the
             * question of formal adoption to the admin panel where somebody can
             * act on it.
             */
            <p className="border-l-2 border-line-strong pl-5 text-[0.9375rem] text-muted">
              {publicStatusLine}
              {updated ? ` Last updated ${formatDate(updated)}.` : ""}
            </p>
          )}

          {visibleBody.length ? (
            <Blocks blocks={visibleBody} className="mt-10" />
          ) : approvalState === "approved" ? (
            <p className="mt-10 text-muted">
              For anything not covered here, get in touch and somebody will answer directly.
            </p>
          ) : null}

          <div className="mt-10">
            <ButtonLink href={contactRoute}>
              Contact HISCUS about this
              <ArrowRight />
            </ButtonLink>
          </div>
        </Reveal>
      </Section>
      <JsonLd
        data={webPageJsonLd({
          title,
          description: lead,
          path: `/${data.slug}`,
        })}
      />
    </>
  );
}

/** A date as a reader writes it, with the ISO value kept for the machine-readable copy. */
function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}
